import React from "react";
import CalculationDetails from "./CalculationDetails";
import { fields as purchaseFields, resultLabels } from "./PurchaseCostPanel";
import { fields as cashFields, labels as cashLabels } from "./ScenarioPanel";

const inputLabels = Object.fromEntries([...purchaseFields, ...cashFields]);
const text = (value) =>
  typeof value === "boolean"
    ? value
      ? "Confirmed"
      : "Not confirmed"
    : String(value).replaceAll("_", " ");
const money = (value) =>
  value === null
    ? "Not achievable with these inputs"
    : new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
      }).format(value);

export default function SavedFinancials({ finances }) {
  if (!finances)
    return (
      <p className="source">No financial scenario saved with this report.</p>
    );
  return (
    <details className="saved-financials">
      <summary>View saved financial scenarios</summary>
      <p>
        Historical calculations from your saved assumptions. These are not
        current quotes or an assessment of concession eligibility. Recalculate
        before relying on changed inputs or rules.
      </p>
      {[
        ["purchase", "Purchase costs"],
        ["cashflow", "Ownership cash flow"],
      ].map(([key, title]) => {
        const result = finances[key];
        if (!result) return null;
        const names = key === "purchase" ? resultLabels : cashLabels;
        return (
          <section key={key} aria-label={`Saved ${title.toLowerCase()}`}>
            <h3>{title}</h3>
            <p className="source">
              {result.calc_version}
              {result.rules_version
                ? ` · ${result.rules_version} · rules checked ${result.rules_checked_on}`
                : ""}
            </p>
            {result.source_url && (
              <a href={result.source_url}>Verify current rules with QRO</a>
            )}
            <h4>Saved assumptions and confirmations</h4>
            <dl>
              {Object.entries(result.inputs).map(([name, value]) => (
                <div className="result-row" key={name}>
                  <dt>{inputLabels[name] || name.replaceAll("_", " ")}</dt>
                  <dd>{text(value)}</dd>
                </div>
              ))}
            </dl>
            <h4>Saved calculation</h4>
            <dl>
              {Object.entries(result.costs || result.base)
                .filter(([name]) => names[name])
                .map(([name, value]) => (
                  <div className="result-row" key={name}>
                    <dt>{names[name]}</dt>
                    <dd>
                      {name.endsWith("_pct") ? `${value}%` : money(value)}
                    </dd>
                  </div>
                ))}
            </dl>
            {result.stress_tests && (
              <>
                <h4>Stress tests: annual pre-tax cash flow</h4>
                <dl>
                  {Object.entries(result.stress_tests).map(([name, stress]) => (
                    <div className="result-row" key={name}>
                      <dt>
                        {
                          {
                            rate_plus_2pct:
                              "Interest rate +2 percentage points",
                            rent_minus_10pct: "Rent −10%",
                            vacancy_plus_4weeks:
                              "Vacancy +4 weeks (capped at 52)",
                            repair_5000: "Additional $5,000 repair",
                          }[name]
                        }
                      </dt>
                      <dd>{money(stress.annual_cashflow)}</dd>
                    </div>
                  ))}
                </dl>
              </>
            )}
            {result.limitations.map((item) => (
              <p className="source" key={item}>
                {item}
              </p>
            ))}
            <CalculationDetails result={result} />
          </section>
        );
      })}
    </details>
  );
}
